import $ from "jquery";
import {GPPlant} from "./gp-plant.js";
import {GPStructure} from "./gp-structure.js";
import {GPStructure3D} from "./gp-structure3d.js";
import {GPCustomObject} from "./gp-customobject.js";
import {GPLabel} from "./gp-label.js";
import {GPProjectView} from "./gp-projectview";
import {get} from 'svelte/store';
import {season} from "./gp";
import {GPTypeCustomTempStructure} from "./gp-typecustomtempstructure";

/**
 *
 * @param {int} backgroundId - set if it is a new project with one of the app's embedded background
 * @param {GPProject} backgroundProject - provide other project if new project should have background cloned from it
 * @param {string }backgroundSrc - set if it is a new project with custom background
 * @param {number} horizonPos
 * @param {number} designWidth
 * @param {int} sizeX - width of background and project
 * @param {int} sizeY - height of background and project
 * @constructor
 */
export let GPProject = function (backgroundId, backgroundProject, backgroundSrc, horizonPos, designWidth, sizeX, sizeY) {
    if (backgroundSrc)
        return this.initFromImage(backgroundSrc, horizonPos, designWidth, sizeX, sizeY);
    if (backgroundProject)
        return this.initFromProject(backgroundProject);
    return this.initFromBackgroundId(backgroundId, sizeX, sizeY);
}

GPProject.prototype.initFromBackgroundId = function (backgroundId, sizeX, sizeY) {
    this.items = [];
    this.itemsById = {};
    this.backgroundId = backgroundId;
    this.background = GP.backgroundsById[backgroundId];
    this.sizeX = sizeX ? sizeX : GPSettings.maxAllowedWidth;
    this.sizeY = sizeY ? sizeY : GPSettings.maxAllowedWidth * 1364 / 2048;
    this.horizonPos = this.background.farLineHeight;
    this.designWidth = this.background.designWidth;
    this.textureCanvas = document.createElement('canvas');
    this.textureCanvas.width = this.sizeX;
    this.textureCanvas.height = this.sizeY;
    this.savedId = null;
    this.savedIOSId = null;
    this.savedPath = null;
    this.title = null;
    this.unsavedChanges = false;
    this.backgroundSrc = this.background.staticGetPathForImage();
    this.backgroundType = "standard";

    if (localStorage['GPSoilSettings']) {
        let s = JSON.parse(localStorage['GPSoilSettings'])
        this.soilTypePH = s["soilTypePH"];
        this.soilTypeQuality =  s["soilTypeQuality"];
        this.soilHumidity =  s["soilHumidity"];
        this.zone = s["zone"];
        this.ctr =  s["countryId"];
        this.region = s["regionId"];
    } else {
        this.soilTypePH = null;
        this.soilTypeQuality = null;
        this.soilHumidity = null;
        this.zone = null;
        this.ctr = null;
        this.region = null;
    }
};

GPProject.prototype.initFromImage = function (imageSrc, horizonPos, designWidth, sizeX, sizeY) {
    this.items = [];
    this.itemsById = {};
    this.backgroundId = null;
    this.sizeX = sizeX;
    this.sizeY = sizeY;
    this.horizonPos = horizonPos;
    this.designWidth = designWidth;
    this.textureCanvas = document.createElement('canvas');
    this.textureCanvas.width = this.sizeX;
    this.textureCanvas.height = this.sizeY;
    this.savedId = null;
    this.savedIOSId = null;
    this.savedPath = null;
    this.title = null;
    this.unsavedChanges = false;
    this.backgroundSrc = imageSrc;
    this.backgroundType = "custom";

    if (localStorage['GPSoilSettings']) {
        let s = JSON.parse(localStorage['GPSoilSettings'])
        this.soilTypePH = s["soilTypePH"];
        this.soilTypeQuality =  s["soilTypeQuality"];
        this.soilHumidity =  s["soilHumidity"];
        this.zone = s["zone"];
        this.ctr =  s["countryId"];
        this.region = s["regionId"];
    } else {
        this.soilTypePH = null;
        this.soilTypeQuality = null;
        this.soilHumidity = null;
        this.zone = null;
        this.ctr = null;
        this.region = null;
    }
};

GPProject.prototype.initFromProject = function (project) {
    this.items = [];
    this.itemsById = {};
    this.backgroundId = project.backgroundId;
    this.background = project.background;
    this.sizeX = project.sizeX;
    this.sizeY = project.sizeY;
    this.horizonPos = project.horizonPos;
    this.designWidth = project.designWidth;
    this.textureCanvas = document.createElement('canvas');
    this.textureCanvas.width = this.sizeX;
    this.textureCanvas.height = this.sizeY;
    this.savedId = project.savedId;
    this.savedIOSId = project.savedIOSId;
    this.savedPath = project.savedPath;
    this.title = project.title;
    this.unsavedChanges = project.unsavedChanges;
    this.backgroundType = project.backgroundType;
    this.backgroundSrc = project.backgroundSrc;
    this.backgroundCanvas = project.backgroundCanvas;
    this.soilTypePH = project.soilTypePH;
    this.soilTypeQuality = project.soilTypeQuality;
    this.soilHumidity = project.soilHumidity;    
    this.zone = project.zone;
    this.ctr = project.ctr;
    this.region = project.region;    
};

GPProject.prototype.getBackgroundCanvas = function() {
    var that = this;
    return new Promise(function(resolve, reject) {
        if (that.backgroundCanvas)
            resolve(that.backgroundCanvas);
        else if (that.backgroundSrc) {
            var img = new Image;
            img.onload = function(){
                that.backgroundCanvas = document.createElement('canvas');
                that.backgroundCanvas.width = that.sizeX;
                that.backgroundCanvas.height = that.sizeY;
                var octx = that.backgroundCanvas.getContext('2d');
                octx.drawImage(img, 0, 0, that.sizeX, that.sizeY);
                resolve(that.backgroundCanvas);
            };
            img.onerror = reject;
            img.src = that.backgroundSrc;
        }
    });
}

GPProject.prototype.generateItemId = function() {
    var idCandidate = 0;
    for (var p in this.items) {
        var itemInstance = this.items[p];
        if (parseInt(itemInstance.id) >= idCandidate) idCandidate = parseInt(itemInstance.id) + 1;
    }
    return idCandidate;
}

GPProject.prototype.plantFitsTo = function (plantType) {
    var fits = true;
    
    var mapping = {
        "GPSoilPHAcid" : "soilTypePH_acid",
        "GPSoilPHNeutral" : "soilTypePH_normal",
        "GPSoilPHAlcaline" : "soilTypePH_base",
        "GPSoilRichnessRich" : "soilTypeQuality_rich",
        "GPSoilRichnessAverage" : "soilTypeQuality_normal",
        "GPSoilRichnessPoor" : "soilTypeQuality_poor",
        "GPSoilHumidityHumid" : "soilHumidity_wet",
        "GPSoilHumidityAverage" : "soilHumidity_normal",
        "GPSoilHumidityDry" : "soilHumidity_arid"
    };
    var zones = ["1a","1b","1","2a","2b","2","3a","3b","3","4a","4b","4","5a","5b","5","6a","6b","6","7a","7b","7","8a","8b","8","9a","9b","9","10a","10b","10","11a","11b","11","12a","12b","12","13a","13b","13","14a","14b","14","15a","15b","15"];
    
    if (this.soilTypePH && !plantType[mapping[this.soilTypePH]]) fits = false;
    if (this.soilTypeQuality && !plantType[mapping[this.soilTypeQuality]]) fits = false;
    if (this.soilHumidity && !plantType[mapping[this.soilHumidity]]) fits = false;
    if (this.zone) {
        var gardenZoneIdx = zones.indexOf(String(this.zone));
        var plantMinZoneIdx = zones.indexOf(plantType.area);
        var plantMaxZoneIdx = zones.indexOf(plantType.area_max);
        if (plantMinZoneIdx < 0) plantMinZoneIdx = 0;
        if (plantMaxZoneIdx < 0) plantMaxZoneIdx = 100;
        if (gardenZoneIdx < plantMinZoneIdx || gardenZoneIdx > plantMaxZoneIdx) fits = false;
    }
    
    return fits;
}

/**
 * Adds new plant to the project
 *
 * @param {int} plantTypeId
 * @param {number} x in project coords
 * @param {number} y in project coords
 * @param {number} scale
 * @param {int|null} id
 *
 * @return {GPPlant}
 */
GPProject.prototype.addPlant = function (plantTypeId, x, y, scale, id) {
    var plant = new GPPlant(id != undefined ? id : this.generateItemId(), plantTypeId, x, y, scale);
    this.items.push(plant);
    this.itemsById[plant.id] = plant;
    return plant;
};

/**
 * Adds new structure (or 3D structure) to the project
 *
 * @param {int} structureTypeId
 * @param {number} x in project coords
 * @param {number} y in project coords
 * @param {number} scale
 * @param {1|-1} flipHorizontal
 * @param {int|null} id
 * @param {number} rotation in rads
 *
 * @return {GPStructure|GPStructure3D}
 */
GPProject.prototype.addStructure = function (structureTypeId, x, y, scale, flipHorizontal, adjustment, id, rotation) {
    if (!GP.structureTypesById[structureTypeId]) return;
    if (!flipHorizontal) flipHorizontal = 1;
    let structure;
    if (!GP.structureTypesById[structureTypeId]["model3d"])
        structure = new GPStructure(id != undefined ? id : this.generateItemId(), structureTypeId, x, y, scale, flipHorizontal, adjustment);
    else
        structure = new GPStructure3D(id != undefined ? id : this.generateItemId(), structureTypeId, x, y, scale, flipHorizontal, adjustment, rotation);
    this.items.push(structure);
    this.itemsById[structure.id] = structure;
    return structure;
};

/**
 * Adds new custom object to the project
 *
 * @param {int} customTypeId
 * @param {number} x in project coords
 * @param {number} y in project coords
 * @param {number} scale
 * @param {1|-1} flipHorizontal
 * @param {boolean} temp for objects loaded with project or cut from background, which are not added to application database
 * @param {int|null} id
 * @param {boolean} fixedObject for objects cut from the background, that cannot be moved
 *
 * @return {GPCustomObject}
 */
GPProject.prototype.addCustom = function (customTypeId, x, y, scale, flipHorizontal, temp, id, fixedObject) {
    if (!flipHorizontal) flipHorizontal = 1;
    var custom = new GPCustomObject(id != undefined ? id : this.generateItemId(), customTypeId, x, y, scale, flipHorizontal, temp, fixedObject);
    this.items.push(custom);
    this.itemsById[custom.id] = custom;
    return custom;
};

/**
 * Adds new label to the project
 *
 * @param {GPItem} originItemInstance parent object
 * @param {string} label
 * @param {number} x in project coords
 * @param {number} y in project coords
 * @param {int|null} id
 *
 * @return {GPLabel}
 */
GPProject.prototype.addLabel = function(originItemInstance, label, x, y, id) {
    var label = new GPLabel(id != undefined ? id : this.generateItemId(), label, x, y, originItemInstance);
    this.items.push(label);
    this.itemsById[label.id] = label;
    return label;
};

GPProject.prototype.moveItem = function (itemId, x, y) {
    var item = this.itemsById[itemId];
    item.x = x;
    item.y = y;
    $(document).trigger('GPItemEdited', item );
};

GPProject.prototype.biggerItem = function (itemId) {
    var item = this.itemsById[itemId];
    item.scale = item.scale * 1.2;
    $(document).trigger('GPItemEdited', item );
};

GPProject.prototype.smallerItem = function (itemId) {
    var item = this.itemsById[itemId];
    item.scale = item.scale / 1.2;
    $(document).trigger('GPItemEdited', item );
};

GPProject.prototype.rotateRightItem = function (itemId) {
    var item = this.itemsById[itemId];
    item.rotation = item.rotation - Math.PI/24;
    $(document).trigger('GPItemHardEdited', item );
};

GPProject.prototype.rotateLeftItem = function (itemId) {
    var item = this.itemsById[itemId];
    item.rotation = item.rotation + Math.PI/24;
    $(document).trigger('GPItemHardEdited', item );
};

GPProject.prototype.flipItem = function (itemId) {
    var item = this.itemsById[itemId];
    item.horizontalFlip = - item.horizontalFlip;
    $(document).trigger('GPItemEdited', item );
};

GPProject.prototype.adjustItem = function (itemId, adjustment) {
    var item = this.itemsById[itemId];
    item.adjustment = adjustment;
    $(document).trigger('GPItemEdited', item );
};

/**
 * Api function for removing elements from the project
 *
 * @param {number} itemId Element to be removed from the project
 */
GPProject.prototype.removeItem = function (itemId) {
    var that = this;
    var item = this.itemsById[itemId];
    if (!item) return;

    this.childrenItems(item).forEach(function (el) {
        that.removeItem(el.id)
    });

    for (var p = this.items.length; p--;) {
        if (this.items[p].id === itemId) {
            this.items.splice(p, 1);
        }
    }
    delete this.itemsById[itemId];
    $(document).trigger('GPItemRemoved', itemId );
};

/**
 * Adds identical object on the project
 * @param {int} itemId - original item id
 * @param {number} offset - optional, x/y offset at which new item is placed
 * @return {GPItem}
 */
GPProject.prototype.cloneItem = function (itemId, offset) {
    let original = this.itemsById[itemId];
    offset = offset ? offset : 10;
    let itemInstance;
    if (original instanceof GPPlant) {
        itemInstance = GP.project.addPlant(original.typeId, original.x+offset, original.y+offset, original.scale);
    }

    if (original instanceof GPStructure || original instanceof GPStructure3D) {
        itemInstance = GP.project.addStructure(original.typeId, original.x+offset, original.y+offset, original.scale, original.horizontalFlip, original.adjustment, null, original.rotation);
    }
    if (original instanceof GPCustomObject) {
        itemInstance = GP.project.addCustom(original.typeId, original.x+offset, original.y+offset, original.scale, original.horizontalFlip);
    }

    return itemInstance;
}

GPProject.prototype.swapItemsOfType = function (oldType, newType) {
    let itemsToUpdate = this.items.filter(pl => pl instanceof GPPlant && pl.typeId === oldType)
    this.swapItems(itemsToUpdate, newType);
}

GPProject.prototype.swapItems = function (itemsToUpdate, newType) {
    itemsToUpdate = itemsToUpdate.filter(pl => pl instanceof GPPlant);
    GP.saveUndo("modifyItems", itemsToUpdate);
    itemsToUpdate.map(pl => {
        pl.typeId = newType;
        pl.data = GP.plantTypesById[newType];
    });
    $(document).trigger('GPPlantsHardEdited');
}

GPProject.prototype.childrenItems = function (item) {
    var children = [];
    for (var p = this.items.length; p--;) {
        if (this.items[p].originItemInstance && this.items[p].originItemInstance.id === item.id)
            children.push(this.items[p]);
    }
    return children;
};

GPProject.prototype.resortItems = function() {
    this.items.sort(function (a,b) {
        if (a instanceof GPLabel && !(b instanceof GPLabel)) return 1;
        if (!(a instanceof GPLabel) && b instanceof GPLabel) return -1;
        return (a.y - a.actualBaseOffset) - (b.y - b.actualBaseOffset);
    });
    var zind = 0;
    for (var p in this.items) {
        if (this.items[p]) {
            this.items[p].zindex = zind;
            zind += 1;
        }
    }
};

GPProject.prototype.plantTypes = function() {
    var plantTypes = [];
    var plantTypesById = {};
    
    for (var i in this.items) {
        var pl = this.items[i];
        if (pl instanceof GPPlant) {
            if (!plantTypesById[pl.typeId]) {
                plantTypes.push({id:pl.typeId, data:pl.data, name:pl.getName()});
                plantTypesById[pl.typeId] = 1;
            } else
                plantTypesById[pl.typeId] += 1;
        }
    }
    plantTypes.forEach(function(e){e["count"]=plantTypesById[e.id]});
    
    return plantTypes;
};

/**
 *
 * @param {boolean} include_background
 * @param {boolean} include_surface
 * @param {boolean} include_items
 * @param {boolean} include_custom
 * @return {string}
 */
GPProject.prototype.jsonify = function (include_background, include_surface, include_items, include_custom) {
    var prj = {};
    prj.version = "gpjs-3";
    prj.default_season = get(season);

    if (include_background) {
        if (this.backgroundType === "standard")
            prj.backgroundId = this.backgroundId;
        else {
            prj.customBG = {
                bgimgSource: this.backgroundCanvas.toDataURL('image/jpeg', 0.7),
                bgdesignWidth: this.designWidth,
                bgfarLineHeight: this.horizonPos
            };
        }
    }

    if (include_items) {
        prj.plant = [];
        prj.equipment = [];
        prj.custom = [];
        prj.customTypes = [];
        prj.label = [];
        for (let i in this.items) {
            let pl = this.items[i];
            if (pl instanceof GPPlant)
                prj.plant.push(pl.toJSON());
            if (pl instanceof GPStructure)
                prj.equipment.push(pl.toJSON());
            if (pl instanceof GPCustomObject) {
                let res = pl.toJSON();
                res.typeStored = include_custom || !!pl.temp;
                prj.custom.push(res);
            }
            if (pl instanceof GPLabel)
                prj.label.push(pl.toJSON());
        }
    }

    //now customObjects images
    if (include_custom) {
        var customTypeExamplesById = {};
        var customTypes = [];
        for (let i in this.items) {
            let pl = this.items[i];
            if (pl instanceof GPCustomObject) {
                if (!customTypeExamplesById[pl.typeId]) {
                    customTypeExamplesById[pl.typeId] = pl;
                    customTypes.push(pl.typeId);
                }
            }
        }    
        for (let i in customTypes) {
            var typeId = customTypes[i];
            var imgW = customTypeExamplesById[typeId].imageWidth;
            var imgH = customTypeExamplesById[typeId].imageHeight;
            var imgData = GPProjectView.getItemImgData(customTypeExamplesById[typeId]).attrs.image;

            var customObjCanvas;
            customObjCanvas = document.createElement('canvas');
            customObjCanvas.width = imgW;
            customObjCanvas.height = imgH;            
            var ctx = customObjCanvas.getContext('2d');
            ctx.drawImage(imgData,0,0);

            var customObjDesc = {
                id: typeId,
                imgData: customObjCanvas.toDataURL('image/webp'),
                type: "webp",
                imgWidth: imgW,
                imgHeight: imgH
            };

            prj.customTypes.push(customObjDesc);
        }
    }

    if (include_surface)
        prj.surfaceData = this.textureCanvas.toDataURL('image/webp');
    
    if (this.ctr) prj.ctr = this.ctr;
    if (this.region) prj.region = this.region;
    if (this.zone) prj.area = this.zone;
    if (this.soilTypePH) prj.soilTypePH = this.soilTypePH;
    if (this.soilTypeQuality) prj.soilTypeQuality = this.soilTypeQuality;
    if (this.soilHumidity) prj.soilHumidity = this.soilHumidity;

    prj.sizeX = this.sizeX;
    prj.sizeY = this.sizeY;

    return JSON.stringify(prj);
};


/**
 *
 * @param {json} projData
 * @param {{"mode": ("load"|"undo")}} params
 * @return {Object}
 */
GPProject.parseFromJson = function(projData, params) {
    if (projData.version === "gpjs-3") return GPProject.parseFromJsonGPJS3(projData, params);
};


/**
 *
 * @param {json} prj
 * @param {{"mode": ("load"|"undo")}} params
 * @return {Object}
 */
GPProject.parseFromJsonGPJS3 = function(prj, params) {
    var mode = !params || params["mode"] === "load" ? "load" : "undo";
    var newProject;

    if (params && params["backgroundFromProject"]) {
        newProject = new GPProject(null, params["backgroundFromProject"]);
    } else {
        if (!GPProject.projectLoadValidateData(prj)) return false;
        if (prj.backgroundId) {
            newProject = new GPProject(prj.backgroundId, null, null, null, null, prj.sizeX, prj.sizeY);
        } else {
            newProject = new GPProject(null, null, prj.customBG.bgimgSource, prj.customBG.bgfarLineHeight, prj.customBG.bgdesignWidth, prj.sizeX?prj.sizeX:1024, prj.sizeY?prj.sizeY:682);
        }
    }

    for (let i in prj.customTypes) {
        let ct = prj.customTypes[i];
        let ctt = new GPTypeCustomTempStructure(ct);
        if (mode==="load") {
            let nextFreeCustomTypeId = 0;
            GP.customObjects.forEach(c => {if (c.id >= nextFreeCustomTypeId) nextFreeCustomTypeId = c.id +1});
            for (let j in prj.custom) {
                if (prj.custom[j].typeId == ctt.id) prj.custom[j].typeId = nextFreeCustomTypeId;
            }
            ctt.id = nextFreeCustomTypeId;
            GP.customObjects.push(ctt);
            GP.customObjectsById[ctt.id] = ctt;
        } //when undo - do not rewrite that data
    }

    for (let i in prj.custom) {
        let pl = prj.custom[i];
        newProject.addCustom(pl.typeId, pl.x, pl.y, pl.s, pl.f, pl.typeStored, pl.id, pl.fixed);
    }

    for (let i in prj.plant) {
        let pl = prj.plant[i];
        newProject.addPlant(pl.typeId, pl.x, pl.y, pl.s, pl.id);
    }

    for (let i in prj.equipment) {
        let pl = prj.equipment[i];
        newProject.addStructure(pl.typeId, pl.x, pl.y, pl.s, pl.f, pl.adj, pl.id, pl.r);
    }

    for (let i in prj.label) {
        let pl = prj.label[i];
        newProject.addLabel(newProject.itemsById[pl.o], pl.t, pl.x, pl.y, pl.id);
    }

    var ctx = newProject.textureCanvas.getContext('2d');
    var img = new Image;
    img.onload = function(){
      ctx.drawImage(img,0,0);
      $(document).trigger('GPSurfaceLoaded' );
    };
    img.src = prj.surfaceData;

    if (prj.ctr) newProject.ctr = prj.ctr;
    if (prj.region) newProject.region = prj.region;
    if (prj.area) newProject.zone = prj.area;
    if (prj.soilTypePH) newProject.soilTypePH = prj.soilTypePH;
    if (prj.soilTypeQuality) newProject.soilTypeQuality = prj.soilTypeQuality;
    if (prj.soilHumidity) newProject.soilHumidity = prj.soilHumidity;

    return newProject;
};


GPProject.convertFMGCoordsRealToScreen = function(bck, px, py, drawingWidth, drawingHeight) {
    var farLineHeightPercent = bck["-farLineHeight"]; 
    var farLineWidthPercent = bck["-farLineWidth"];
    var designWidth = bck["-designWidth"];
    var designHeight = bck["-designHeight"];
    var scaledDrawingHeight = drawingHeight;
    var scaledDrawingWidth = drawingWidth;        
    var scale = drawingWidth/designWidth;
    var scaleY = drawingHeight/designHeight;        

    var yn = py * (scaleY * farLineHeightPercent);
    var persp = 1 - (yn / (drawingHeight * farLineHeightPercent)) * (1-farLineWidthPercent);
    var xn = px * scale * persp;

    var x = xn + scaledDrawingWidth / 2;
    var y = scaledDrawingHeight - yn;

    return ({x:x, y:y});
};

GPProject.projectLoadValidateData = function(prj) {
    var valid = true;
    if (!prj.customBG && !GP.backgroundsById[prj.backgroundId]) valid=false;
    //TODO verify plants & equipment
    return valid;
};
